import { TaxIdType } from '@zorro/clients';
import { VALIDATION_MESSAGES } from '@zorro/common-utils';
import { TabButtonGroup } from '@zorro/design-system/v2/TabButtonGroup';
import { TextInput } from '@zorro/design-system/v2/TextInput';
import {
  Box,
  FormErrorMessage,
  Group,
  InputWrapper,
} from '@zorro/web/design-system';
import {
  Control,
  Controller,
  FieldErrors,
  FieldPath,
  Path,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import * as yup from 'yup';

import styles from './TaxIdInput.module.css';

const einPattern = /^\d{9}$/u;
const itinPattern = /^9\d{8}$/u;

type TaxIdFormFields = {
  taxIdType?: TaxIdType | null;
  taxId?: string;
};

export const taxIdExclusions: [string, string][] = [
  ['taxId', 'taxIdType'],
  ['taxIdType', 'taxId'],
];

export const taxIdSchemaObject = {
  taxIdType: yup
    .mixed<TaxIdType>()
    .oneOf(Object.values(TaxIdType), VALIDATION_MESSAGES.taxIdTypeRequired)
    .typeError(VALIDATION_MESSAGES.taxIdTypeRequired)
    .when('taxId', {
      is: (taxId: string) => Boolean(taxId),
      then: (schema) => schema.required(VALIDATION_MESSAGES.taxIdTypeRequired),
      otherwise: (schema) => schema.nullable(),
    }),
  taxId: yup.string().when('taxIdType', ([taxIdType], schema) => {
    switch (taxIdType) {
      case TaxIdType.EIN: {
        return schema.test(
          'ein-format',
          'Invalid EIN',
          (value) => !value || einPattern.test(value)
        );
      }
      case TaxIdType.ITIN: {
        return schema.test(
          'itin-format',
          'Invalid ITIN',
          (value) => !value || itinPattern.test(value)
        );
      }
      default: {
        return schema.nullable();
      }
    }
  }),
};

type Props<T extends TaxIdFormFields> = {
  control: Control<T>;
  watch: UseFormWatch<T>;
  errors: FieldErrors<T>;
  trigger: UseFormTrigger<T>;
  isRequired?: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
};

export const TaxIdInput = <T extends TaxIdFormFields>({
  watch,
  errors,
  trigger,
  control,
  isRequired = false,
  isDisabled = false,
  isReadOnly = false,
}: Props<T>) => {
  const taxId = watch('taxId' as FieldPath<T>);
  const taxIdType = watch('taxIdType' as FieldPath<T>);

  const handleTaxIdTypeChange = (
    newTaxIdType: string | null,
    onChange: (newValue: string) => void
  ) => {
    if (newTaxIdType) {
      onChange(newTaxIdType);
    }
    if (taxId) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      trigger('taxId' as FieldPath<T>);
    }
  };

  let taxIdTypeError = false;
  if ('taxIdType' in errors) taxIdTypeError = true;

  return (
    <>
      <Group wrap="nowrap" gap="xs" align="flex-end">
        <Controller
          control={control}
          disabled={isDisabled}
          name={'taxIdType' as FieldPath<T>}
          render={({ field: { onChange, value, ...rest } }) => (
            <InputWrapper
              label="Tax ID"
              required={isRequired}
              w="40%"
              className={styles.taxIdTypeWrapper}
            >
              {isReadOnly ? (
                <Box>{value}</Box>
              ) : (
                <TabButtonGroup
                  {...rest}
                  isFullWidth
                  size="md"
                  value={value}
                  onChange={(val) => handleTaxIdTypeChange(val, onChange)}
                >
                  <TabButtonGroup.List grow>
                    {Object.keys(TaxIdType).map((key) => (
                      <TabButtonGroup.Tab
                        key={key}
                        value={key}
                        disabled={isDisabled}
                      >
                        {key}
                      </TabButtonGroup.Tab>
                    ))}
                  </TabButtonGroup.List>
                </TabButtonGroup>
              )}
            </InputWrapper>
          )}
        />
        <Controller
          control={control}
          disabled={isDisabled}
          name={'taxId' as FieldPath<T>}
          render={({ field: { value, ...rest } }) => (
            <TextInput
              {...rest}
              isReadOnly={isReadOnly}
              value={value || undefined}
              label=" "
              placeholder={`Type ${taxIdType} number`}
              size="md"
              w="60%"
            />
          )}
        />
      </Group>
      {taxIdTypeError && (
        <FormErrorMessage fieldName={'taxIdType' as Path<T>} errors={errors} />
      )}
      {!taxIdTypeError && (
        <FormErrorMessage fieldName={'taxId' as Path<T>} errors={errors} />
      )}
    </>
  );
};
